import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useSettings } from '../contexts/SettingsContext';

const API = (process.env.REACT_APP_API_URL || 'http://187.127.34.239:4001/api').replace(/\/$/, '');
const BASE_URL = API.replace('/api', '');

const ALLOWED = ['application/pdf','image/jpg','image/jpeg','image/png','image/webp'];
const ALLOWED_EXT = ['.pdf','.jpg','.jpeg','.png','.webp'];
const MAX_MB = 20;

const COLUNAS = [
  { id: 'DOCUMENTACAO_PENDENTE', label: '⚠️ DOCUMENTOS PENDENTES', color: '#DC2626' },
  { id: 'DOCUMENTACAO_RECEBIDA', label: '🟦 DOCUMENTAÇÃO RECEBIDA', color: '#3B82F6' },
  { id: 'EM_PRODUCAO',           label: '🟨 EM PRODUÇÃO',           color: '#EAB308' },
  { id: 'ATRASADO',              label: '🟧 ATRASADO',              color: '#F97316' },
  { id: 'FINALIZADO',            label: '🟩 FINALIZADO',            color: '#22C55E' },
];

const CHECKLIST_PROD = [
  { key:'documentos_enviados_serasa', label:'Documentos enviados para liberar SERASA' },
  { key:'sistema_serasa_liberado',     label:'Sistema SERASA Liberado' },
  { key:'atualizacao_cadastral',      label:'Atualização Cadastral' },
  { key:'atualizacao_endereco',       label:'Atualização Endereço' },
  { key:'atualizacao_dados_pessoais', label:'Atualização Dados Pessoais' },
  { key:'vida_financeira',            label:'Vida Financeira' },
  { key:'verificar_email',            label:'Verificar E-mail' },
  { key:'consulta_seguro',            label:'Consulta Seguro' },
  { key:'antes',                      label:'Antes' },
  { key:'cadastro',                   label:'Cadastro' },
  { key:'depois',                     label:'Depois' },
];

const fmt = d => {
  if (!d) return '—';

  const texto =
    String(d).trim();

  /*
   * Data pura YYYY-MM-DD.
   * Montar diretamente evita inversão por timezone.
   */
  const dataPura =
    texto.match(
      /^(\d{4})-(\d{2})-(\d{2})$/
    );

  if (dataPura) {
    return (
      dataPura[3] +
      '/' +
      dataPura[2] +
      '/' +
      dataPura[1]
    );
  }

  /*
   * Se já estiver DD/MM/AAAA,
   * manter como está.
   */
  if (
    /^\d{2}\/\d{2}\/\d{4}$/
      .test(texto)
  ) {
    return texto;
  }

  const data =
    new Date(d);

  if (
    Number.isNaN(
      data.getTime()
    )
  ) {
    return texto;
  }

  return data.toLocaleDateString(
    'pt-BR',
    {
      timeZone:
        'America/Sao_Paulo'
    }
  );
};

const formatarDocumentoPainel = valor => {
  const original =
    String(valor ?? '').trim();

  const digitos =
    original.replace(/\D/g, '');

  if (digitos.length === 11) {
    return digitos.replace(
      /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
      '$1.$2.$3-$4'
    );
  }

  if (digitos.length === 14) {
    return digitos.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      '$1.$2.$3/$4-$5'
    );
  }

  return original;
};

const formatarTituloEleitorPainel = valor => {
  const original =
    String(valor ?? '').trim();

  const digitos =
    original.replace(/\D/g, '');

  if (digitos.length === 12) {
    return digitos.replace(
      /^(\d{4})(\d{4})(\d{4})$/,
      '$1 $2 $3'
    );
  }

  return original;
};

const formatarTelefonePainel = valor => {
  /*
   * Outros Telefones pode chegar
   * como uma lista.
   */
  if (Array.isArray(valor)) {
    return valor.map(
      item =>
        formatarTelefonePainel(item)
    );
  }

  if (
    valor !== null &&
    typeof valor === 'object'
  ) {
    return valor;
  }

  const original =
    String(valor ?? '').trim();

  if (!original) {
    return original;
  }

  let digitos =
    original.replace(/\D/g, '');

  /*
   * Remove DDI 55 somente
   * de números brasileiros completos.
   */
  if (
    digitos.startsWith('55') &&
    (
      digitos.length === 12 ||
      digitos.length === 13
    )
  ) {
    digitos =
      digitos.slice(2);
  }

  /*
   * Celular:
   * 62 + 99156 + 3615
   * =>
   * (62)99156-3615
   */
  if (digitos.length === 11) {
    return (
      '(' +
      digitos.slice(0, 2) +
      ')' +
      digitos.slice(2, 7) +
      '-' +
      digitos.slice(7)
    );
  }

  /*
   * Fixo:
   * 62 + 3256 + 3615
   * =>
   * (62)3256-3615
   */
  if (digitos.length === 10) {
    return (
      '(' +
      digitos.slice(0, 2) +
      ')' +
      digitos.slice(2, 6) +
      '-' +
      digitos.slice(6)
    );
  }

  return original;
};

const formatarCampoPainel = (
  rotulo,
  valor
) => {
  if (
    valor === undefined ||
    valor === null ||
    valor === ''
  ) {
    return valor;
  }

  const nome =
    String(rotulo || '');

  /*
   * CPF/CNPJ:
   * titular, empresa, sócio,
   * cônjuge e sócios adicionais.
   */
  if (
    /\bCPF\b|\bCNPJ\b/i
      .test(nome)
  ) {
    return formatarDocumentoPainel(
      valor
    );
  }

  /*
   * Título eleitoral:
   * 035149041538
   * =>
   * 0351 4904 1538
   */
  if (
    /t[ií]tulo de eleitor/i
      .test(nome)
  ) {
    return formatarTituloEleitorPainel(
      valor
    );
  }

  /*
   * Telefones brasileiros.
   */
  if (
    /telefone|celular|whatsapp/i
      .test(nome)
  ) {
    return formatarTelefonePainel(
      valor
    );
  }

  /*
   * Datas exibidas no painel.
   * Não afeta "Dia de Recebimento".
   */
  if (
    /expedi[cç][aã]o|nascimento|vencimento|assinado em|confirmado em|in[ií]cio|conclus[aã]o/i
      .test(nome)
  ) {
    return fmt(valor);
  }

  return valor;
};
const fmtBytes = b => !b ? '' : b < 1024 ? `${b}B` : b < 1048576 ? `${(b/1024).toFixed(1)}KB` : `${(b/1048576).toFixed(1)}MB`;

function fileIcon(name) {
  if (/\.(jpg|jpeg|png|webp)/i.test(name)) return '🖼️';
  if (/\.pdf/i.test(name)) return '📄';
  return '📎';
}

// ─── DRAWER ──────────────────────────────────────────────────────────────────
export function CardDrawer({ lead, coluna, onClose, onColumnChange, onReset, onDelete }) {
  const { darkMode } = useSettings();
  const [tab, setTab] = useState('docs');
  const [checklist, setChecklist] = useState({});
  const [docs, setDocs] = useState([]);
  const [obs, setObs] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [detailsError, setDetailsError] = useState('');
  const obsTimer = useRef(null);
  const fileRef = useRef();

  const bg  = darkMode ? '#1F2937' : '#FFFFFF';
  const txt = darkMode ? '#F9FAFB' : '#111827';
  const sub = darkMode ? '#9CA3AF' : '#6B7280';
  const bdr = darkMode ? '#374151' : '#E5E7EB';
  const row = darkMode ? '#374151' : '#F9FAFB';

  const valorVisivel = (valor) => {
    if (
      valor === undefined ||
      valor === null ||
      valor === ''
    ) return null;

    if (valor === true) return 'Sim';
    if (valor === false) return 'Não';

    if (Array.isArray(valor)) {
      return valor.length
        ? valor.map(item =>
            typeof item === 'object'
              ? JSON.stringify(item)
              : String(item)
          ).join(', ')
        : null;
    }

    if (typeof valor === 'object') {
      return JSON.stringify(valor);
    }

    return String(valor);
  };

  const LinhaInfo = ({ rotulo, valor }) => {
    const valorFormatado =
      formatarCampoPainel(
        rotulo,
        valor
      );

    const exibido =
      valorVisivel(
        valorFormatado
      );
    if (!exibido) return null;

    return (
      <div style={{
        display:'grid',
        gridTemplateColumns:'145px 1fr',
        gap:10,
        padding:'8px 0',
        borderBottom:`1px solid ${bdr}`
      }}>
        <span style={{ color:sub, fontSize:12 }}>
          {rotulo}
        </span>
        <span style={{
          color:txt,
          fontSize:13,
          fontWeight:500,
          wordBreak:'break-word'
        }}>
          {exibido}
        </span>
      </div>
    );
  };

  const SecaoInfo = ({ titulo, children }) => (
    <div style={{
      marginBottom:18,
      padding:14,
      border:`1px solid ${bdr}`,
      borderRadius:10,
      background:row
    }}>
      <div style={{
        fontWeight:800,
        fontSize:14,
        marginBottom:8,
        color:txt
      }}>
        {titulo}
      </div>
      {children}
    </div>
  );

  useEffect(() => {
    let ativo = true;

    setDetailsLoading(true);
    setDetailsError('');

    Promise.all([
      axios.get(`${API}/crm/${lead.id}/checklist`),
      axios.get(`${API}/crm/${lead.id}/documents`),
      axios.get(`${API}/crm/${lead.id}/obs`),
      axios.get(`${API}/crm/${lead.id}/details`)
    ])
      .then(([checklistResp, docsResp, obsResp, detailsResp]) => {
        if (!ativo) return;

        setChecklist(
          checklistResp.data?.data ||
          checklistResp.data ||
          {}
        );

        setDocs(
          docsResp.data?.data ||
          docsResp.data ||
          []
        );

        setObs(
          obsResp.data?.obs ||
          ''
        );

        setDetails(
          detailsResp.data?.data ||
          null
        );
      })
      .catch(error => {
        if (!ativo) return;

        console.error(
          '[CRM] Erro ao carregar ficha completa:',
          error
        );

        setDetailsError(
          error.response?.data?.error ||
          'Não foi possível carregar a ficha completa.'
        );
      })
      .finally(() => {
        if (ativo) {
          setDetailsLoading(false);
        }
      });

    return () => {
      ativo = false;
    };
  }, [lead.id]);

  const toggleCheck = async (key, val) => {
    const anterior = { ...checklist };
    const next = { ...checklist, [key]: val };
    setChecklist(next);

    try {
      const r = await axios.patch(
        `${API}/crm/${lead.id}/checklist`,
        { key, value: val }
      );
      if (r.data?.allDone) {
        onColumnChange(lead.id, "FINALIZADO");
      }
    } catch (e) {
      setChecklist(anterior);
      alert(
        e.response?.data?.error ||
        "Não foi possível atualizar o checklist."
      );
    }
  };

  const saveObs = (v) => {
    setObs(v);
    clearTimeout(obsTimer.current);
    obsTimer.current = setTimeout(async () => {
      try {
        await axios.patch(`${API}/crm/${lead.id}/obs`, { obs: v });
      } catch (e) {
        alert(e.response?.data?.error || "Não foi possível salvar a observação.");
      }
    }, 800);
  };

  const validateFile = (file) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) return `Extensão não permitida: ${ext}`;
    if (file.size > MAX_MB * 1024 * 1024) return `Arquivo excede ${MAX_MB}MB`;
    return null;
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const err = validateFile(file);
    if (err) { setUploadErr(err); fileRef.current.value = ''; return; }
    setUploadErr('');
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/crm/${lead.id}/documents`, fd);
      setDocs(prev => [...prev, r.data?.data || r.data]);
    } catch(e) {
      setUploadErr(e.response?.data?.error || 'Erro no upload');
    }
    setUploading(false);
    fileRef.current.value = '';
  };

  const handleDelete = async (filename) => {
    if (window.confirm("Excluir este documento?") === false) return;
    try {
      await axios.delete(`${API}/crm/${lead.id}/documents/${filename}`);
      setDocs(prev => prev.filter(f => f.filename !== filename));
    } catch (e) {
      alert(e.response?.data?.error || "Não foi possível excluir o documento.");
    }
  };

  const prodPct = () => {
    const done = CHECKLIST_PROD.filter(i => checklist[i.key]).length;
    return Math.round((done / CHECKLIST_PROD.length) * 100);
  };

  const TABS = [
    { id:'docs',      label:'📁 Documentos' },
    { id:'checklist', label:'✅ Checklist' },
    { id:'obs',       label:'📝 Obs.' },
    { id:'info',      label:'ℹ️ Info' },
  ];

  return (
    <div style={{ position:'fixed', inset:0, zIndex:1000, display:'flex' }}>
      <div onClick={onClose} style={{ flex:1, background:'rgba(0,0,0,0.45)' }} />
      <div style={{
        width: '100%', maxWidth: 560, background: bg, color: txt,
        display:'flex', flexDirection:'column',
        boxShadow:'-6px 0 32px rgba(0,0,0,0.25)', overflowY:'auto'
      }}>
        {/* Header */}
        <div style={{ padding:'20px 24px 0', borderBottom:`1px solid ${bdr}`, flexShrink:0 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <div style={{ fontSize:20, fontWeight:700 }}>{lead.nome || 'Sem nome'}</div>
              <div style={{ fontSize:13, color:sub, marginTop:2 }}>📱 {formatarTelefonePainel(lead.phone)}</div>
              {(lead.contexto?.cpf || lead.contexto?.cpf_cnpj || lead.cpf_cnpj) &&
                <div style={{ fontSize:13, color:sub }}>🪪 {formatarDocumentoPainel(lead.contexto?.cpf || lead.contexto?.cpf_cnpj || lead.cpf_cnpj)}</div>}
            </div>
            <button onClick={onClose} style={{
              background:'transparent', border:'none', fontSize:22,
              cursor:'pointer', color:sub, lineHeight:1
            }}>✕</button>
          </div>
          <div style={{ display:'flex', gap:4, marginTop:14, flexWrap:'wrap' }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                padding:'7px 13px', border:'none', cursor:'pointer', borderRadius:'6px 6px 0 0',
                fontWeight: tab===t.id ? 700 : 400, fontSize:13,
                background: tab===t.id ? (darkMode?'#374151':'#EFF6FF') : 'transparent',
                color: tab===t.id ? '#3B82F6' : sub
              }}>{t.label}</button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding:'20px 24px', flex:1 }}>

          {/* DOCUMENTOS */}
          {tab==='docs' && (
            <div>
              <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap', alignItems:'center' }}>
                <button onClick={() => fileRef.current.click()} style={{
                  background:'#3B82F6', color:'#fff', border:'none', borderRadius:8,
                  padding:'8px 16px', cursor:'pointer', fontWeight:600, fontSize:13
                }}>{uploading ? '⏳ Enviando…' : '+ Anexar'}</button>
                <input ref={fileRef} type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  style={{ display:'none' }} onChange={handleUpload} />
                {docs.length > 0 && (
                  <a href={`${API}/crm/${lead.id}/documents/zip`}
                    style={{ background:'#6B7280', color:'#fff', textDecoration:'none',
                      borderRadius:8, padding:'8px 14px', fontSize:13, fontWeight:600 }}>
                    ⬇ Baixar ZIP
                  </a>
                )}
              </div>
              {uploadErr && (
                <div style={{ background:'#FEF2F2', color:'#B91C1C', borderRadius:6,
                  padding:'8px 12px', fontSize:13, marginBottom:10 }}>{uploadErr}</div>
              )}
              <div style={{ fontSize:11, color:sub, marginBottom:10 }}>
                Permitido: PDF, JPG, JPEG, PNG, WEBP · Máximo: {MAX_MB}MB
              </div>
              {docs.length === 0 ? (
                <div style={{ textAlign:'center', color:sub, padding:'40px 0', fontSize:14 }}>
                  Nenhum documento anexado
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {docs.map(f => f && (
                    <div key={f.filename} style={{
                      display:'flex', alignItems:'center', gap:10, padding:'10px 14px',
                      borderRadius:8, background:row, border:`1px solid ${bdr}`
                    }}>
                      <span style={{ fontSize:20, flexShrink:0 }}>{fileIcon(f.name)}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:600, overflow:'hidden',
                          textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name}</div>
                        <div style={{ fontSize:11, color:sub }}>{fmtBytes(f.size)}</div>
                      </div>
                      {/* Visualizar */}
                      <a href={`${API}/crm/${lead.id}/documents/${encodeURIComponent(f.filename)}/view`} target="_blank" rel="noreferrer"
                        title="Visualizar" style={{
                          color:'#3B82F6', fontSize:12, fontWeight:700,
                          textDecoration:'none', padding:'4px 8px',
                          borderRadius:6, border:`1px solid ${bdr}`
                        }}>Ver</a>
                      {/* Download */}
                      <a href={`${API}/crm/${lead.id}/documents/${encodeURIComponent(f.filename)}/download`}
                        title="Download" style={{
                          color:'#10B981', fontSize:12, fontWeight:700,
                          textDecoration:'none', padding:'4px 8px',
                          borderRadius:6, border:`1px solid ${bdr}`, marginLeft:4
                        }}>↓</a>
                      {/* Excluir */}
                      <button onClick={() => handleDelete(f.filename)}
                        title="Excluir" style={{
                          background:'transparent', border:'none', color:'#EF4444',
                          cursor:'pointer', fontSize:16, marginLeft:4, padding:'4px'
                        }}>🗑</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* CHECKLIST */}
          {/* CHECKLIST */}
          {tab==='checklist' && (
            <div>
              {coluna !== 'FINALIZADO' && (
                <>
                  <div style={{ fontWeight:700, marginBottom:6, fontSize:15 }}>Checklist de Produção</div>
                  <div style={{ marginBottom:18 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:sub, marginBottom:4 }}>
                      <span>Progresso</span><span>{prodPct()}%</span>
                    </div>
                    <div style={{ height:10, background: darkMode?'#374151':'#E5E7EB', borderRadius:5, overflow:'hidden' }}>
                      <div style={{ height:'100%', background:'#22C55E', width:`${prodPct()}%`, transition:'width .35s ease' }} />
                    </div>
                  </div>
                  {CHECKLIST_PROD.map(item => (
                    <label key={item.key} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14, cursor:'pointer' }}>
                      <input type='checkbox' checked={!!checklist[item.key]}
                        onChange={e => toggleCheck(item.key, e.target.checked)}
                        style={{ width:18, height:18, cursor:'pointer', accentColor:'#22C55E' }} />
                      <span style={{ fontSize:14,
                        color: checklist[item.key] ? '#22C55E' : txt,
                        textDecoration: checklist[item.key] ? 'line-through' : 'none'
                      }}>{item.label}</span>
                    </label>
                  ))}
                </>
              )}

              {coluna === 'FINALIZADO' && (
                <div style={{ textAlign:'center', padding:'40px 0' }}>
                  <div style={{ fontSize:52 }}>✅</div>
                  <div style={{ fontWeight:800, fontSize:20, color:'#22C55E', marginTop:10 }}>SERVIÇO ENTREGUE</div>
                  <div style={{ color:sub, fontSize:13, marginTop:12 }}>Início: {fmt(lead.created_at)}</div>
                  <div style={{ color:sub, fontSize:13 }}>Conclusão: {fmt(lead.updated_at)}</div>
                </div>
              )}
            </div>
          )}

          {/* OBSERVAÇÕES */}
          {tab==='obs' && (
            <div>
              <div style={{ fontWeight:700, marginBottom:10, fontSize:15 }}>Observações</div>
              <textarea value={obs} onChange={e => saveObs(e.target.value)}
                placeholder="Digite observações sobre este cliente…"
                style={{
                  width:'100%', minHeight:220, padding:12, borderRadius:8, resize:'vertical',
                  border:`1px solid ${bdr}`, background: darkMode?'#374151':'#F9FAFB',
                  color:txt, fontSize:14, boxSizing:'border-box', fontFamily:'inherit'
                }} />
              <div style={{ fontSize:11, color:sub, marginTop:4 }}>💾 Salvo automaticamente</div>
            </div>
          )}

          {/* INFO */}
          {tab==='info' && (
            <div>
              <div style={{
                display:'flex',
                justifyContent:'space-between',
                alignItems:'center',
                gap:10,
                flexWrap:'wrap',
                marginBottom:14
              }}>
                <div style={{ fontWeight:800, fontSize:16 }}>
                  Ficha Operacional do Cliente
                </div>

                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const resposta = await axios.get(
                        `${API}/crm/${lead.id}/report.pdf`,
                        {
                          responseType: 'blob'
                        }
                      );

                      const url = window.URL.createObjectURL(
                        new Blob(
                          [resposta.data],
                          { type: 'application/pdf' }
                        )
                      );

                      const link = document.createElement('a');
                      link.href = url;
                      link.download =
                        `ficha-operacional-${lead.nome || lead.id}.pdf`;

                      document.body.appendChild(link);
                      link.click();
                      link.remove();

                      window.URL.revokeObjectURL(url);
                    } catch (error) {
                      console.error(
                        '[CRM] Erro ao gerar Ficha PDF:',
                        error
                      );

                      alert(
                        error.response?.data?.error ||
                        'Não foi possível gerar a Ficha PDF.'
                      );
                    }
                  }}
                  style={{
                    padding:'8px 12px',
                    borderRadius:8,
                    border:`1px solid ${bdr}`,
                    background:bg,
                    color:txt,
                    cursor:'pointer',
                    fontWeight:700
                  }}
                >
                  📄 Gerar Ficha PDF
                </button>

                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const resposta =
                        await axios.get(
                          `${API}/crm/${lead.id}/rating.pdf`,
                          {
                            responseType: 'blob'
                          }
                        );

                      const url =
                        window.URL.createObjectURL(
                          new Blob(
                            [resposta.data],
                            {
                              type:
                                'application/pdf'
                            }
                          )
                        );

                      const link =
                        document.createElement(
                          'a'
                        );

                      const tipo =
                        details?.cadastro?.tipo_pessoa ||
                        lead.tipo_pessoa;

                      const titulo =
                        tipo === 'PJ'
                          ? 'RATING Pessoa Juridica'
                          : 'RATING Pessoa Fisica';

                      link.href = url;
                      link.download =
                        `${titulo} - ${lead.nome || lead.id}.pdf`;

                      document.body.appendChild(
                        link
                      );

                      link.click();
                      link.remove();

                      window.URL.revokeObjectURL(
                        url
                      );
                    } catch (error) {
                      console.error(
                        '[CRM] Erro ao gerar PDF de Rating:',
                        error
                      );

                      alert(
                        error.response?.data?.error ||
                        'Não foi possível gerar o PDF de Rating.'
                      );
                    }
                  }}
                  style={{
                    padding:'8px 12px',
                    borderRadius:8,
                    border:`1px solid ${bdr}`,
                    background:'#17365D',
                    color:'#FFFFFF',
                    cursor:'pointer',
                    fontWeight:700
                  }}
                >
                  📊 Gerar RATING PDF
                </button>
              </div>

              {detailsLoading && (
                <div style={{ color:sub, padding:12 }}>
                  Carregando ficha completa…
                </div>
              )}

              {detailsError && (
                <div style={{
                  padding:12,
                  borderRadius:8,
                  background:'#FEE2E2',
                  color:'#991B1B',
                  marginBottom:12
                }}>
                  {detailsError}
                </div>
              )}

              {!detailsLoading && details && (() => {
                const coleta = details.coleta || {};

                const temValor = valor =>
                  valor !== undefined &&
                  valor !== null &&
                  valor !== '' &&
                  (
                    !Array.isArray(valor) ||
                    valor.length > 0
                  );

                const linhasIniciais = [
                  {
                    rotulo:'Nome',
                    valor:
                      details.cadastro?.nome ||
                      details.lead?.nome
                  },
                  {
                    rotulo:'Tipo',
                    valor:
                      details.cadastro?.tipo_pessoa ||
                      details.lead?.tipo_pessoa
                  },
                  {
                    rotulo:'CPF',
                    valor:details.cadastro?.cpf
                  },
                  {
                    rotulo:'CNPJ',
                    valor:
                      details.cadastro?.cnpj ||
                      details.empresa?.cnpj
                  },
                  {
                    rotulo:'CPF do Sócio Administrador',
                    valor:
                      details.socio_administrador?.cpf
                  },
                  {
                    rotulo:'E-mail',
                    valor:
                      coleta.coleta_email ||
                      details.cadastro?.email ||
                      details.lead?.email
                  },
                  {
                    rotulo:'Tipo do Documento',
                    valor:coleta.doc_tipo
                  },
                  {
                    rotulo:'Número do RG',
                    valor:
                      coleta.numero_rg ||
                      coleta.rg_numero ||
                      coleta.rg
                  },
                  {
                    rotulo:'Estado do RG',
                    valor:
                      coleta.estado_rg ||
                      coleta.rg_estado ||
                      coleta.uf_rg
                  },
                  {
                    rotulo:'Expedição do RG',
                    valor:
                      coleta.expedicao_rg ||
                      coleta.rg_expedicao ||
                      coleta.data_expedicao_rg
                  },
                  {
                    rotulo:'Documento Frente',
                    valor:
                      coleta.doc_frente_recebida
                        ? 'Recebido'
                        : null
                  },
                  {
                    rotulo:'Documento Verso',
                    valor:
                      coleta.doc_verso_recebido
                        ? 'Recebido'
                        : null
                  },
                  {
                    rotulo:'Selfie com Documento',
                    valor:
                      coleta.selfie_recebida
                        ? 'Recebida'
                        : null
                  },
                  {
                      rotulo:'CPF impresso no RG',
                      valor:
                        (
                          coleta.cpf_no_rg === '1' ||
                          coleta.rg_tem_cpf === 'sim'
                        )
                          ? 'Sim'
                          : (
                              coleta.cpf_no_rg === '2' ||
                              coleta.rg_tem_cpf === 'nao'
                                ? 'Não'
                                : (
                                    coleta.cpf_no_rg ||
                                    coleta.rg_tem_cpf
                                  )
                            )
                    },
                    {
                    rotulo:'Documento do CPF',
                    valor:
                      coleta.cpf_recebido ||
                        coleta.documento_cpf_recebido ||
                        coleta.cpf_documento_recebido
                        ? 'Recebido'
                        : null
                  },
                  {
                    rotulo:'Senha SERASA',
                    valor:
                      coleta.senha_serasa ||
                      coleta.serasa_senha ||
                      coleta.senhaSerasa
                  },
                  {
                    rotulo:'Título de Eleitor',
                    valor:coleta.titulo_eleitor
                  },
                    {
                      rotulo:'Sexo',
                      valor:coleta.sexo
                    },
                    {
                      rotulo:'Data de Nascimento',
                      valor:coleta.data_nascimento
                    },
                    {
                      rotulo:'Nome do Pai',
                      valor:coleta.nome_pai
                    },
                    {
                      rotulo:'Nome da Mãe',
                      valor:coleta.nome_mae
                    },
                  {
                    rotulo:'Escolaridade',
                    valor:coleta.escolaridade
                  },
                  {
                    rotulo:'Profissão',
                    valor:coleta.profissao
                  },
                  {
                    rotulo:'Média Salarial',
                    valor:coleta.media_salarial
                  },
                  {
                    rotulo:'Dia de Recebimento',
                    valor:coleta.dia_pagamento
                  },
                  {
                    rotulo:'Estado Civil',
                    valor:
                      coleta.estado_civil_texto ||
                      coleta.estado_civil
                  },
                  {
                    rotulo:'Nome do Cônjuge',
                    valor:coleta.conjuge_nome
                  },
                  {
                    rotulo:'CPF do Cônjuge',
                    valor:coleta.conjuge_cpf
                  },
                  {
                    rotulo:'Nascimento do Cônjuge',
                    valor:coleta.conjuge_nascimento
                  },
                  {
                    rotulo:'Certidão de Casamento',
                    valor:
                      coleta.certidao_recebida
                        ? 'Recebida'
                        : null
                  },
                  {
                    rotulo:'Dependentes',
                    valor:coleta.dependentes
                  },
                  {
                      rotulo:'CEP',
                      valor:coleta.cep
                    },
                    {
                      rotulo:'Endereço Residencial',
                      valor:coleta.endereco
                    },
                    {
                      rotulo:'Bairro',
                      valor:coleta.bairro
                    },
                    {
                      rotulo:'Município',
                      valor:
                        coleta.municipio ||
                        coleta.cidade
                    },
                    {
                      rotulo:'Estado',
                      valor:coleta.estado
                    },
                    {
                    rotulo:'Telefone Principal',
                    valor:
                      coleta.telefone_principal ||
                      details.cadastro?.telefone ||
                      details.lead?.phone
                  },
                    {
                      rotulo:'Telefone Residencial',
                      valor:
                        coleta.telefone_residencial
                    },
                    {
                      rotulo:'Telefone Comercial',
                      valor:
                        coleta.telefone_comercial
                    },
                  {
                    rotulo:'Outros Telefones',
                    valor:
                      coleta.outros_telefones_numeros
                  },
                  {
                    rotulo:'Comprovante de Endereço',
                    valor:
                      coleta.comprovante_recebido
                        ? 'Recebido'
                        : null
                  },
                  {
                      rotulo:'Tipo de Imóvel',
                      valor:
                        coleta.situacao_imovel
                    },
                    {
                      rotulo:'Possui imóvel em seu nome?',
                      valor:
                        coleta.possui_imoveis === 'sim'
                          ? 'Sim'
                          : (
                              coleta.possui_imoveis === 'nao'
                                ? 'Não'
                                : coleta.possui_imoveis
                            )
                    },
                    {
                      rotulo:'Localização da Residência Atual',
                      valor:
                        coleta.localizacao_imovel_alugado
                    }
                ];

                const linhasEmpresa = [
                  {
                    rotulo:'Razão Social',
                    valor:details.empresa?.razao_social
                  },
                  {
                    rotulo:'Nome Fantasia',
                    valor:details.empresa?.nome_fantasia
                  },
                  {
                    rotulo:'CNAE',
                    valor:details.empresa?.cnae
                  },
                  {
                    rotulo:'Faturamento',
                    valor:details.empresa?.faturamento
                  },
                  {
                    rotulo:'Nome do Sócio Administrador',
                    valor:
                      details.socio_administrador?.nome
                  }
                ];

                const contrato =
                  details.contracts?.[0] || {};

                const pagamento =
                  details.payments?.[0] || {};

                const pix =
                  details.pix_facilitado?.[0] || {};

                const cobranca =
                  details.cobrancas?.[0] || {};

                const linhasFinais = [
                  {
                    rotulo:'Contrato',
                    valor:contrato.status
                  },
                  {
                    rotulo:'Contrato assinado em',
                    valor:
                      contrato.signed_at
                        ? fmt(contrato.signed_at)
                        : null
                  },
                  {
                    rotulo:'Forma de Pagamento',
                    valor:
                      pagamento.payment_method ||
                      coleta.forma_pagamento_escolhida ||
                      coleta.forma_pagamento
                  },
                  {
                    rotulo:'Valor Pago',
                    valor:pagamento.amount
                  },
                  {
                    rotulo:'Status do Pagamento',
                    valor:pagamento.status
                  },
                  {
                    rotulo:'Pagamento confirmado em',
                    valor:
                      pagamento.paid_at
                        ? fmt(pagamento.paid_at)
                        : null
                  },
                  {
                    rotulo:'Valor da Entrada',
                    valor:pix.valor_entrada
                  },
                  {
                    rotulo:'Valor da Segunda Parcela',
                    valor:
                      pix.valor_segunda_parc ||
                      cobranca.valor_parcela
                  },
                  {
                    rotulo:'Vencimento da Segunda Parcela',
                    valor:
                      pix.data_vencimento_segunda ||
                      cobranca.data_vencimento
                  },
                  {
                    rotulo:'Status da Segunda Parcela',
                    valor:
                      pix.status ||
                      cobranca.status
                  }
                ];

                const renderLinhas = (
                  itens,
                  prefixo
                ) =>
                  itens
                    .filter(item =>
                      temValor(item.valor)
                    )
                    .map((item, index) => (
                      <LinhaInfo
                        key={`${prefixo}-${index}-${item.rotulo}`}
                        rotulo={item.rotulo}
                        valor={item.valor}
                      />
                    ));

                const imoveis =
                  details.patrimonio?.imoveis || [];

                const veiculos =
                  details.patrimonio?.veiculos || [];

                const socios =
                  details.socios_adicionais || [];

                return (
                  <div style={{
                    border:`1px solid ${bdr}`,
                    borderRadius:10,
                    padding:'8px 14px',
                    background:
                      darkMode
                        ? '#374151'
                        : '#F9FAFB'
                  }}>
                    {renderLinhas(
                      linhasIniciais,
                      'inicial'
                    )}

                    {imoveis.map((item, index) => (
                      <React.Fragment
                        key={`imovel-${index}`}
                      >
                        <LinhaInfo
                          rotulo={`Imóvel ${index + 1} - Tipo`}
                          valor={item?.tipo}
                        />
                        <LinhaInfo
                          rotulo={`Imóvel ${index + 1} - Localização`}
                          valor={item?.localizacao}
                        />
                        <LinhaInfo
                          rotulo={`Imóvel ${index + 1} - Valor`}
                          valor={item?.valor}
                        />
                      </React.Fragment>
                    ))}

                    {veiculos.map((item, index) => (
                      <React.Fragment
                        key={`veiculo-${index}`}
                      >
                        <LinhaInfo
                          rotulo={`Veículo ${index + 1} - Placa`}
                          valor={item?.placa}
                        />
                        <LinhaInfo
                          rotulo={`Veículo ${index + 1} - Ano`}
                          valor={item?.ano}
                        />
                        <LinhaInfo
                          rotulo={`Veículo ${index + 1} - Estado da Placa`}
                          valor={item?.estado_placa}
                        />
                        <LinhaInfo
                          rotulo={`Veículo ${index + 1} - Valor`}
                          valor={item?.valor}
                        />
                      </React.Fragment>
                    ))}

                    {renderLinhas(
                      linhasEmpresa,
                      'empresa'
                    )}

                    {socios.map((socio, index) => (
                      <React.Fragment
                        key={`socio-${index}`}
                      >
                        <LinhaInfo
                          rotulo={`Sócio adicional ${index + 1} - Nome`}
                          valor={
                            socio?.nome ||
                            socio?.name
                          }
                        />
                        <LinhaInfo
                          rotulo={`Sócio adicional ${index + 1} - CPF`}
                          valor={
                            socio?.cpf ||
                            socio?.documento
                          }
                        />
                        <LinhaInfo
                          rotulo={`Sócio adicional ${index + 1} - Participação`}
                          valor={
                            socio?.participacao ||
                            socio?.percentual
                          }
                        />
                      </React.Fragment>
                    ))}

                    {renderLinhas(
                      linhasFinais,
                      'final'
                    )}
                  </div>
                );
              })()}

              <div style={{
                marginTop:20,
                display:'flex',
                gap:10,
                flexWrap:'wrap'
              }}>
                <button onClick={onReset} style={{
                  padding:'9px 18px',
                  borderRadius:8,
                  border:'none',
                  cursor:'pointer',
                  background:'#F59E0B',
                  color:'#fff',
                  fontWeight:700,
                  fontSize:13
                }}>
                  Resetar Lead
                </button>

                <button onClick={onDelete} style={{
                  padding:'9px 18px',
                  borderRadius:8,
                  border:'none',
                  cursor:'pointer',
                  background:'#EF4444',
                  color:'#fff',
                  fontWeight:700,
                  fontSize:13
                }}>
                  Excluir Lead
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── CARD ─────────────────────────────────────────────────────────────────────
function KanbanCard({ lead, coluna, onOpen, onMove }) {
  const { darkMode } = useSettings();
  const bg  = darkMode ? '#1F2937' : '#FFFFFF';
  const txt = darkMode ? '#F9FAFB' : '#111827';
  const sub = darkMode ? '#9CA3AF' : '#6B7280';
  const bdr = darkMode ? '#374151' : '#E5E7EB';
  const cpf = formatarDocumentoPainel(
    lead.contexto?.cpf ||
    lead.contexto?.cpf_cnpj ||
    lead.cpf_cnpj
  );

  return (
    <div onClick={() => onOpen(lead)} style={{
      background:bg, borderRadius:10, padding:'14px 16px', cursor:'pointer',
      boxShadow:'0 1px 4px rgba(0,0,0,0.08)', border:`1px solid ${bdr}`,
      transition:'box-shadow .15s, transform .1s'
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow='0 4px 14px rgba(0,0,0,0.14)'; e.currentTarget.style.transform='translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow='0 1px 4px rgba(0,0,0,0.08)'; e.currentTarget.style.transform='none'; }}
    >
      <div style={{ fontWeight:700, fontSize:14, color:txt, marginBottom:4 }}>
        {lead.nome || 'Sem nome'}
      </div>
      <div style={{ fontSize:12, color:sub }}>📱 {formatarTelefonePainel(lead.phone)}</div>
      {cpf && <div style={{ fontSize:12, color:sub }}>🪪 {cpf}</div>}

      {lead.partner_id && (
        <div style={{
          display:'inline-flex',
          alignItems:'center',
          marginTop:7,
          padding:'3px 8px',
          borderRadius:999,
          background:'#7C3AED22',
          color:'#A78BFA',
          border:'1px solid #7C3AED55',
          fontSize:10,
          fontWeight:800
        }}>
          PARCEIRO · {lead.partner_name || 'Não identificado'}
        </div>
      )}

      <div style={{ fontSize:11, color:sub, marginTop:6 }}>
        {lead.producao_iniciada_em
          ? `Produção: ${fmt(lead.producao_iniciada_em)} - ${lead.dias_producao} dia(s)`
          : `Entrada: ${fmt(lead.created_at)}`}
      </div>
      {lead.sla_status === "ALERTA_7_DIAS" && (
        <div style={{ marginTop:6, color:"#FACC15", fontSize:11, fontWeight:800 }}>⚠️ ALERTA: 7 dias em produção</div>
      )}
      {lead.sla_status === "PRAZO_ESTOURADO" && (
        <div style={{ marginTop:6, color:"#EF4444", fontSize:11, fontWeight:800 }}>🚨 PRAZO MÁXIMO DE 10 DIAS EXCEDIDO</div>
      )}
      <div style={{ display:'flex', gap:4, marginTop:10, flexWrap:'wrap' }} onClick={e => e.stopPropagation()}>
        {COLUNAS.filter(c => c.id !== coluna).map(c => (
          <button key={c.id} onClick={() => onMove(lead.id, c.id)} style={{
            fontSize:10, padding:'3px 8px', borderRadius:5, border:`1px solid ${bdr}`,
            background:'transparent', color:sub, cursor:'pointer'
          }}>→ {c.label.split(' ').slice(1).join(' ')}</button>
        ))}
      </div>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function CRM() {
  const { darkMode } = useSettings();
  const [kanban, setKanban] = useState({ DOCUMENTACAO_PENDENTE:[], DOCUMENTACAO_RECEBIDA:[], EM_PRODUCAO:[], ATRASADO:[], FINALIZADO:[] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedCol, setSelectedCol] = useState(null);
  const [search, setSearch] = useState('');



  const bg    = darkMode ? '#111827' : '#F3F4F6';
  const txt   = darkMode ? '#F9FAFB' : '#111827';
  const sub   = darkMode ? '#9CA3AF' : '#6B7280';
  const colBg = darkMode ? '#1A2332' : '#F0F4FF';

  const fetchKanban = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/crm/kanban`);
      setKanban(r.data?.data || r.data || { DOCUMENTACAO_PENDENTE:[], DOCUMENTACAO_RECEBIDA:[], EM_PRODUCAO:[], ATRASADO:[], FINALIZADO:[] });
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchKanban();
  }, [fetchKanban]);

  const handleOpen = (lead, col) => { setSelected(lead); setSelectedCol(col); };

  const handleClose = () => {
    setSelected(null);
    fetchKanban(); // atualiza somente ao fechar o drawer
  };

  const handleMove = async (leadId, coluna) => {
    try {
    await axios.patch(`${API}/crm/${leadId}/coluna`, { coluna });
    // Atualiza apenas o card movido localmente (sem reload completo)
    setKanban(prev => {
      const next = { ...prev };
      let movedLead = null;
      for (const col of Object.keys(next)) {
        const idx = next[col].findIndex(l => l.id === leadId);
        if (idx !== -1) { movedLead = { ...next[col][idx], status_kanban: coluna }; next[col] = next[col].filter((_,i) => i !== idx); break; }
      }
      if (movedLead && next[coluna]) next[coluna] = [movedLead, ...next[coluna]];
      return next;
    });
    } catch (e) {
      alert(
        e.response?.data?.error ||
        "Não foi possível mover o cliente."
      );
      await fetchKanban();
    }
  };

  const handleColumnChange = (leadId, coluna) => handleMove(leadId, coluna);

  const handleReset = async (lead) => {
    if (!window.confirm(`Resetar o lead "${lead.nome || lead.phone}"? O fluxo do bot será reiniciado.`)) return;
    try {
      await axios.post(`${API}/leads/${lead.id}/reset`, { phone: lead.phone });
      alert('Lead resetado com sucesso. Próxima mensagem inicia o fluxo do zero.');
      setSelected(null);
      fetchKanban();
    } catch (e) {
      alert('Erro ao resetar lead: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleDelete = async (lead) => {
    if (!window.confirm(`EXCLUIR PERMANENTEMENTE o lead "${lead.nome || lead.phone}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await axios.delete(`${API}/leads/${lead.id}`, { data: { phone: lead.phone } });
      alert('Lead excluído com sucesso.');
      setSelected(null);
      fetchKanban();
    } catch (e) {
      alert('Erro ao excluir lead: ' + (e.response?.data?.error || e.message));
    }
  };

  const filterLeads = (leads) => {
    if (!search.trim()) return leads;
    const q = search.toLowerCase();
    return leads.filter(l =>
      (l.nome||'').toLowerCase().includes(q) ||
      (l.phone||'').includes(q) ||
      JSON.stringify(l.contexto||{}).toLowerCase().includes(q)
    );
  };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', color:txt, fontSize:16 }}>
      Carregando Esteira…
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', background:bg, padding:'24px 20px', boxSizing:'border-box' }}>
      <div style={{ marginBottom:20 }}>
        <h1 style={{
          fontSize:24,
          fontWeight:800,
          color:txt,
          margin:0
        }}>
          Esteira de Produção
        </h1>

        <p style={{
          color:sub,
          margin:'4px 0 0',
          fontSize:14
        }}>
          Gestão pós-venda · Esteira de Produção
        </p>
      </div>

      {/* Busca */}
      <div style={{ marginBottom:20 }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍  Buscar por nome, telefone ou CPF…"
          style={{
            width:'100%', maxWidth:380, padding:'9px 14px', borderRadius:8, outline:'none',
            border:`1px solid ${darkMode?'#374151':'#D1D5DB'}`,
            background: darkMode?'#1F2937':'#fff', color:txt, fontSize:14
          }} />
      </div>

      {/* Kanban */}
      <div style={{ display:'flex', gap:14, alignItems:'flex-start', overflowX:'auto', paddingBottom:16 }}>
        {COLUNAS.map(col => {
          const leads = filterLeads(kanban[col.id] || []);
          return (
            <div key={col.id} style={{
              minWidth:270, maxWidth:290, flex:'0 0 270px', borderRadius:12,
              background:colBg, padding:'12px 10px', border:`2px solid ${col.color}28`
            }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                <span style={{ fontWeight:700, fontSize:12, color:col.color }}>{col.label}</span>
                <span style={{ background:col.color, color:'#fff', borderRadius:12,
                  padding:'1px 9px', fontSize:12, fontWeight:700 }}>{leads.length}</span>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {leads.length === 0 && (
                  <div style={{ textAlign:'center', color: darkMode?'#4B5563':'#9CA3AF',
                    fontSize:13, padding:'24px 0' }}>Nenhum card</div>
                )}
                {leads.map(lead => (
                  <KanbanCard key={lead.id} lead={lead} coluna={col.id}
                    onOpen={l => handleOpen(l, col.id)}
                    onMove={handleMove} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {selected && (
        <CardDrawer lead={selected} coluna={selectedCol}
          onClose={handleClose} onColumnChange={handleColumnChange}
          onReset={() => handleReset(selected)} onDelete={() => handleDelete(selected)} />
      )}
    </div>
  );
}
